import { useSearchResults, useSearchFilters, useSearchContext } from '../contexts/SearchContext';
import { Hit } from './Hit/index.tsx';
import type { DisplayJob } from '../types';
import BannerModule from './BannerModule';
import { reactGaEvent } from '../utils/cookie-consent/react-ga-safe';
import React from 'react';
import SavedStateContext from '../contexts/SavedStateContext';
// Step 4: Lazy load MapView so mapbox-gl and map UI load only when user switches to map view
const MapViewLazy = React.lazy(() =>
  import('./MapView').then((m) => ({ default: m.MapView }))
);
import { PerformanceProfiler, useRenderLogger } from '../utils/shared/performance-profiler';
import { FormattedMessage } from 'react-intl';
import { SrpResultsGridSkeleton } from './search/SrpResultsGridSkeleton';

interface CustomHitsProps {
  withBanners?: boolean;
  viewMode?: 'list' | 'map';
}

const staticBanners = [
  <BannerModule key="banner-save" text="Save jobs you are interested in to compare salaries and benefits side-by-side." />,
  <BannerModule
    key="banner-login"
    text="Log in and add your current job details to see how your salary compares to new opportunities in Finland."
    onClickEvent={async () => {
      // Send a custom event
      reactGaEvent({
        category: 'interactions',
        action: 'click_login_banner',
        label: 'search_results',
        nonInteraction: false,
      });
      // TODO:: Re-enable auth0
      // const { loginWithRedirect } = useAuth0();
      // await loginWithRedirect({
      //   appState: {
      //     returnTo: '/en/user-profile-overview',
      //   },
      // });
    }}
  />,
];

// Custom comparison function for React.memo to prevent unnecessary re-renders
// CustomHits re-renders are controlled by the context hooks inside, so we only
// need to check if the props themselves changed
function areCustomHitsPropsEqual(prevProps: CustomHitsProps, nextProps: CustomHitsProps): boolean {
  const withBannersSame = prevProps.withBanners === nextProps.withBanners;
  const viewModeSame = prevProps.viewMode === nextProps.viewMode;
  return withBannersSame && viewModeSame;
}

export const CustomHits = React.memo(function CustomHits({ withBanners = false, viewMode: propViewMode }: CustomHitsProps) {
  const renderCountRef = React.useRef(0);
  const isFirstRender = renderCountRef.current === 0;
  renderCountRef.current += 1;
  const prevContextValueRef = React.useRef<any>(null);
  const prevResultsRef = React.useRef<any>(null);
  const prevResultsHitsRef = React.useRef<any[] | null>(null);
  
  const { results } = useSearchResults();
  const { filters } = useSearchFilters();
  const contextValue = useSearchContext();
  const { isSearchLoading } = contextValue;
  
  // Track what's changing from hooks (only on UPDATE renders)
  const prevResultsFromHookRef = React.useRef(results);
  const prevFiltersFromHookRef = React.useRef(filters);
  
  if (!isFirstRender && typeof window !== 'undefined') {
    prevResultsFromHookRef.current = results;
    prevFiltersFromHookRef.current = filters;
    
    // Check if contextValue reference changed
    const contextValueRefChanged = prevContextValueRef.current !== contextValue;
    
    // Track contextValue reference changes (only on updates)
    if (contextValueRefChanged) {
      const prevCtx = prevContextValueRef.current;
      const changes: string[] = [];
      
      if (prevCtx) {
        if (prevCtx.query !== contextValue.query) changes.push('query');
        if (prevCtx.page !== contextValue.page) changes.push('page');
        if (prevCtx.sort !== contextValue.sort) changes.push('sort');
        if (prevCtx.viewMode !== contextValue.viewMode) changes.push('viewMode');
        if (prevCtx.results !== contextValue.results) {
          changes.push('results');
          // Check if hit object refs changed (same IDs but new objects)
          const prevHits = prevCtx.results?.hits || [];
          const currHits = contextValue.results?.hits || [];
          if (prevHits.length === currHits.length) {
            const hitRefsChanged = prevHits.some((prevHit: any, idx: number) => {
              const currHit = currHits[idx];
              return prevHit !== currHit && (prevHit?.objectID || prevHit?.id) === (currHit?.objectID || currHit?.id);
            });
            if (hitRefsChanged) {
              changes.push('results.hits[*] (object refs changed)');
            }
          }
        }
        if (prevCtx.filters !== contextValue.filters) changes.push('filters');
      } else {
        changes.push('(prev context was null)');
      }
    }
    prevContextValueRef.current = contextValue;
    prevResultsHitsRef.current = results?.hits || null;
    prevResultsRef.current = results;
  } else {
    // Initialize refs on first render
    prevContextValueRef.current = contextValue;
    prevResultsRef.current = results;
    prevResultsHitsRef.current = results?.hits || null;
  }
  const { viewMode: contextViewMode, setMapBounds } = contextValue;
  
  // Determine viewMode for variant computation
  // IMPORTANT: SearchProvider initializes AFTER LayoutContent, so contextViewMode may be undefined on first render
  // We MUST use propViewMode (from SSR-initialized useViewMode) for initial render, not contextViewMode
  // propViewMode comes from useViewMode which is initialized synchronously from SSR initialLayout (from cookies)
  // useViewMode always returns a valid ViewMode (defaults to 'list' if initialLayout is undefined)
  // 
  // For initial render: Use propViewMode only (SSR data available at mount time)
  // For subsequent renders: Can use contextViewMode as fallback (after SearchProvider has initialized)
  const viewMode: 'list' | 'map' = propViewMode ?? 'list';
  
  // Compute variant from viewMode synchronously - ensures it's available from first render
  // This prevents variant from being undefined on first render, then changing to compact
  // Always return a valid variant, never undefined - viewMode is 'list' | 'map'
  const variant: 'card' | 'compact' = viewMode === 'list' ? 'compact' : 'card';
  
  // Runtime assertion - variant should ALWAYS be defined at this point
  // If it's not, something is seriously wrong with the initialization
  if (variant !== 'compact' && variant !== 'card') {
    throw new Error(`[CustomHits] CRITICAL: Invalid variant computed: ${variant}. viewMode: ${viewMode}, propViewMode: ${propViewMode}`);
  }
  
  
  const savedState = React.useContext(SavedStateContext);
  
  // Stable reference map for hit objects - keyed by objectID to preserve references
  const hitsMapRef = React.useRef<Map<string, DisplayJob>>(new Map());
  
  // Memoize allHits and stabilize individual hit object references
  const allHits = React.useMemo(() => {
    const rawHits = results?.hits;
    const newHits = Array.isArray(rawHits) ? rawHits : [];
    const hitsMap = hitsMapRef.current;
    const stableHits: DisplayJob[] = [];
    let reusedCount = 0;
    let newCount = 0;
    
    // For each hit, use existing reference if available, otherwise add to map
    // This is critical - if results?.hits gets a new array with new object references,
    // we want to reuse the old object references to prevent Hit components from re-rendering
    for (const hit of newHits) {
      const hitId = hit?.objectID || hit?.id;
      if (hitId) {
        const hitIdStr = String(hitId);
        const existingHit = hitsMap.get(hitIdStr);
        // If we have an existing hit with same ID, ALWAYS use that reference
        // This prevents re-renders when hit objects get new references but same content
        if (existingHit) {
          // Verify the ID still matches (safety check)
          const existingHitId = existingHit.objectID || existingHit.id;
          if (String(existingHitId) === hitIdStr) {
            // Always use existing hit reference (even if same as new hit)
            stableHits.push(existingHit);
            if (existingHit !== hit) {
              reusedCount++;
            }
            // If same reference, just continue (already pushed)
          } else {
            // ID changed - update the map
            hitsMap.set(hitIdStr, hit);
            stableHits.push(hit);
            newCount++;
          }
        } else {
          // New hit - add to map
          hitsMap.set(hitIdStr, hit);
          stableHits.push(hit);
          newCount++;
        }
      } else {
        stableHits.push(hit);
        newCount++;
      }
    }
    
    // Clean up map - remove hits that are no longer in results
    const currentIds = new Set(newHits.map(h => String(h?.objectID || h?.id || '')));
    let removedCount = 0;
    for (const [id] of hitsMap) {
      if (!currentIds.has(id)) {
        hitsMap.delete(id);
        removedCount++;
      }
    }
    
    return stableHits;
  }, [results?.hits]);
  
  // Memoize filterObj to prevent creating new object reference on every render
  // Use a stable empty object to prevent reference changes
  const emptyFilterObj = React.useMemo(() => {
    return {};
  }, []);
  const filterObj = React.useMemo(() => {
    return (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : emptyFilterObj;
  }, [filters, emptyFilterObj]);
  
  // Memoize exclude flags to prevent recalculation
  const excludeSaved = React.useMemo(() => {
    return filterObj.excludeSaved?.includes('true') || false;
  }, [filterObj.excludeSaved]);

  const excludeViewed = React.useMemo(() => {
    return filterObj.excludeViewed?.includes('true') || false;
  }, [filterObj.excludeViewed]);

  const excludeApplied = React.useMemo(() => {
    return filterObj.excludeApplied?.includes('true') || false;
  }, [filterObj.excludeApplied]);
  
  // Stable reference for filtered hits - only recreate if content actually changed
  const hitsRef = React.useRef<DisplayJob[]>([]);
  const hitsVersionRef = React.useRef<number>(0);
  const prevSlicedHitsRef = React.useRef<DisplayJob[]>([]);
  
  // Filter hits based on exclude filters only
  // Hidden jobs are already filtered out server-side in +data.ts
  const hits = React.useMemo(() => {
    const filtered = allHits.filter((hit: DisplayJob) => {
        const jobId = hit.objectID || hit.id;
        if (!jobId) return true;
        
        // Exclude if in saved jobs and excludeSaved is true
        if (excludeSaved && savedState?.saved?.has(String(jobId))) {
          return false;
        }
        
        // Exclude if in viewed jobs and excludeViewed is true
        if (excludeViewed && savedState?.viewed?.has(String(jobId))) {
          return false;
        }
        
        // Exclude if in applied jobs and excludeApplied is true
        if (excludeApplied && savedState?.applied?.has(String(jobId))) {
          return false;
        }
        
        return true;
      });
    
    // Check if the filtered results are actually different from previous
    const prevHits = hitsRef.current;
    const hitsChanged = 
      prevHits.length !== filtered.length ||
      prevHits.some((prevHit, idx) => {
        const currHit = filtered[idx];
        return (prevHit.objectID || prevHit.id) !== (currHit.objectID || currHit.id) ||
               prevHit !== currHit; // Check reference too
      });
    
    if (hitsChanged) {
      hitsRef.current = filtered;
      hitsVersionRef.current += 1;
    }
    
    // Return stable reference if content hasn't changed
    const finalHits = hitsChanged ? filtered : prevHits;
    
    // Check if the hits array is the same as previous (same objects, same order)
    const prevHitsArray = prevSlicedHitsRef.current;
    const hitsArrayChanged = 
      prevHitsArray.length !== finalHits.length ||
      prevHitsArray.some((prevHit, idx) => {
        const currHit = finalHits[idx];
        return prevHit !== currHit || (prevHit.objectID || prevHit.id) !== (currHit.objectID || currHit.id);
      });
    
    if (hitsArrayChanged) {
      prevSlicedHitsRef.current = finalHits;
      // Only log on UPDATE renders
      if (typeof window !== 'undefined' && renderCountRef.current > 1) {
        const prevIds = prevHitsArray.map(h => h.objectID || h.id);
        const currIds = finalHits.map(h => h.objectID || h.id);
        const idsChanged = prevIds.join(',') !== currIds.join(',');
        const refsChanged = prevHitsArray.some((prev, idx) => prev !== finalHits[idx]);
      }
    } else {
      // Return previous array reference to maintain stability
      return prevHitsArray;
    }
    
    return finalHits;
  }, [allHits, excludeSaved, excludeViewed, excludeApplied, savedState, viewMode]);
  
  // Detailed performance logging - track ALL dependencies that could cause re-renders
  // Moved after hits definition to avoid temporal dead zone error
  useRenderLogger('CustomHits', {
    // Props
    withBanners,
    propViewMode,
    // Context values
    viewMode,
    contextViewMode,
    // Results
    resultsReference: results,
    resultsNbHits: results?.nbHits,
    resultsPage: results?.page,
    resultsNbPages: results?.nbPages,
    hitsCount: results?.hits?.length,
    allHitsLength: allHits.length,
    hitsLength: hits.length,
    // Filters
    filtersReference: filters,
    filtersType: typeof filters,
    filterObjKeys: Object.keys(filterObj),
    filterObjReference: filterObj,
    excludeSaved,
    excludeViewed,
    excludeApplied,
    // Saved state
    savedStateReference: savedState,
    savedStateSavedSize: savedState?.saved?.size,
    savedStateViewedSize: savedState?.viewed?.size,
    savedStateAppliedSize: savedState?.applied?.size,
    // Context callbacks (check if they're changing)
    setMapBoundsReference: setMapBounds,
    contextValueReference: contextValue,
  });
  
  // Track previous values to detect what's changing
  const prevValuesRef = React.useRef<{
    results?: any;
    resultsHits?: any[];
    filters?: any;
    savedState?: any;
    contextValue?: any;
    hits?: DisplayJob[];
    hitsLength?: number;
  }>({});
  
  // Track previous values to detect changes
  React.useEffect(() => {
    // Skip on first render
    if (renderCountRef.current === 1) {
      prevValuesRef.current = {
        results,
        resultsHits: results?.hits,
        filters,
        savedState,
        contextValue,
        hits,
        hitsLength: hits.length,
      };
      return;
    }
    
    const prev = prevValuesRef.current;
    const changes: string[] = [];
    
    if (prev.results !== results) {
      changes.push(`results ref changed (prev: ${prev.results?.nbHits}, curr: ${results?.nbHits})`);
    }
    if (prev.resultsHits !== results?.hits) {
      changes.push(`results.hits ref changed (prev length: ${prev.resultsHits?.length}, curr: ${results?.hits?.length})`);
    }
    if (prev.filters !== filters) {
      changes.push('filters ref changed');
    }
    if (prev.savedState !== savedState) {
      changes.push('savedState ref changed');
    }
    if (prev.contextValue !== contextValue) {
      changes.push('contextValue ref changed');
    }
    if (prev.hits !== hits) {
      changes.push(`hits array ref changed (prev length: ${prev.hitsLength}, curr: ${hits.length})`);
    }
    if (prev.hitsLength !== hits.length) {
      changes.push(`hits length changed (${prev.hitsLength} -> ${hits.length})`);
    }
    
    // Update previous values
    prevValuesRef.current = {
      results,
      resultsHits: results?.hits,
      filters,
      savedState,
      contextValue,
      hits,
      hitsLength: hits.length,
    };
  });
  
  // Memoize sendEvent to prevent recreating on every render (causes Hit components to re-render)
  // Use a ref to ensure the function reference is truly stable across all renders
  const sendEventRef = React.useRef<() => void>(() => {});
  const sendEvent = React.useCallback(() => {
    return sendEventRef.current();
  }, []);
  
  // Track sendEvent reference for stability
  const prevSendEventRef = React.useRef(sendEvent);
  prevSendEventRef.current = sendEvent;

  // Memoize map bounds change handler to prevent recreating on every render
  const handleMapBoundsChange = React.useCallback((bounds: any) => {
    // TODO: Trigger geofence search with bounds
    setMapBounds(bounds);
  }, [setMapBounds]);

  // Note: No-results handling is done by the NoResults component in the parent
  // This component should not show its own no-results message
  // However, we still need to render something to maintain layout
  // The parent will conditionally render NoResults instead of CustomHits when appropriate
  // Exception: Always render MapView in map mode, even with no hits
  if (hits.length === 0 && viewMode !== 'map') {
    if (isSearchLoading) {
      return (
        <PerformanceProfiler id="CustomHits">
          <section className="srp-results" aria-labelledby="results-title">
            <h2 id="results-title" className="sr-only">
              <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
            </h2>
            <SrpResultsGridSkeleton />
          </section>
        </PerformanceProfiler>
      );
    }
    // Return empty div to maintain layout, parent will handle showing NoResults
    return <div className="w-full" />;
  }

  const renderContent = () => {
    if (!withBanners) {
      if (viewMode === 'map') {
        return (
          <div className="w-full sm:px-0 md:px-4 lg:px-4 xl:px-6 2xl:px-8">
            <React.Suspense fallback={<div className="min-h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg" aria-hidden />}>
              <MapViewLazy
                jobs={hits as DisplayJob[]}
                onBoundsChange={handleMapBoundsChange}
              />
            </React.Suspense>
          </div>
        );
      }
      
      return (
        <section className="srp-results" aria-labelledby="results-title">
          <h2 id="results-title" className="sr-only">
            <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
          </h2>
          <ul className="srp-results-grid">
            {hits.map((hit, index) => (
              <li
                key={(hit as unknown as DisplayJob).objectID}
                className="srp-results-grid-item"
              >
                <Hit hit={hit as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
              </li>
            ))}
          </ul>
        </section>
      );
    }

    // Responsive rendering with banners
    if (viewMode === 'map') {
      return (
        <div className="w-full max-w-full overflow-x-hidden @sm:px-0 @md:px-4 @lg:px-4 @xl:px-6 @2xl:px-8">
          <React.Suspense fallback={<div className="min-h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg" aria-hidden />}>
            <MapViewLazy
              jobs={hits as DisplayJob[]}
              onBoundsChange={handleMapBoundsChange}
            />
          </React.Suspense>
        </div>
      );
    }
    
    return (
      <section className="w-full max-w-full overflow-x-hidden @sm:px-0 @md:px-4 @lg:px-4 @xl:px-6 @2xl:px-8 flex flex-col gap-6 @lg:grid @lg:grid-cols-[2fr_1fr] @xl:grid-cols-[3fr_1fr]" aria-labelledby="results-title">
        <h2 id="results-title" className="sr-only">
          <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
        </h2>
        {/* Main hits flow: responsive grid (md:2, lg:3, xl:4 columns) */}
        <ul className="srp-results-grid">
          {/* Small screens (< lg): interleave banners after 5th hit when present, else just hits */}
          <div className="@lg:hidden contents">
            {staticBanners.length > 0
              ? (() => {
                  const elements = [];
                  const interval = Math.ceil((hits.length - 5) / staticBanners.length);
                  let bannerIdx = 0;
                  for (let idx = 0; idx < hits.length; idx++) {
                    elements.push(
                      <li
                        key={(hits[idx] as unknown as DisplayJob).objectID}
                        className="srp-results-grid-item"
                      >
                        <Hit hit={hits[idx] as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={idx} />
                      </li>,
                    );
                    if (idx >= 5 && bannerIdx < staticBanners.length && (idx - 5) % interval === 0) {
                      elements.push(
                        <li key={`banner-${bannerIdx}`}>
                          {React.cloneElement(staticBanners[bannerIdx], { key: `banner-${bannerIdx}` })}
                        </li>
                      );
                      bannerIdx++;
                    }
                  }
                  return elements;
                })()
              : hits.map((hit, index) => (
                  <li
                    key={(hit as unknown as DisplayJob).objectID}
                    className="srp-results-grid-item"
                  >
                    <Hit hit={hit as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
                  </li>
                ))}
          </div>
          <div className="hidden @lg:contents">
            {hits.map((hit, index) => (
              <li
                key={(hit as unknown as DisplayJob).objectID}
                className="srp-results-grid-item"
              >
                <Hit hit={hit as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
              </li>
            ))}
          </div>
        </ul>
        {/* Large screens: sticky banners on the right */}
        <div className="hidden @lg:flex flex-col gap-4 sticky top-24 h-fit">{staticBanners.map((banner) => banner)}</div>
      </section>
    );
  };

  return (
    <PerformanceProfiler id="CustomHits">
      {renderContent()}
    </PerformanceProfiler>
  );
}, areCustomHitsPropsEqual);
